import { useEffect, useState, useContext } from "react";
import { SelectedOpenPartOrdersContext } from "../../../../contexts/parts/selectedOpenPartOrders.part.context";
import DEFAULT_OPEN_ORDERS_COLUMNS from "../../../../constants/parts/defaultOpenOrdersColumns";
import { OpenPartOrdersContext } from "../../../../contexts/parts/openPartOrders.context";
import { VendorsContext } from "../../../../contexts/vendors.context";
import ORDERED_PART_STATUSES from "../../../../constants/parts/orderedPartStatuses.part.constant";
import PAYMENT_METHODS from "../../../../constants/parts/paymentMethods.part.constant";
import WARRANTY_TYPES from "../../../../constants/parts/warrantyTypes.part.constant";
import useReturnTableComponents from "../../../../hooks/other/useReturnTableComponents";
import useOpenParts from "../../../../hooks/parts/useOpenParts.parts.hook";

const DEFAULT_FORM_FIELDS = {
  requested_date: "",
  ticket_number: "",
  link: "",
  vendor_part_number: "",
  description: "",
  warranty_type: "",
  status: "",
  ordered_date: "",
  vendor: "",
  po_number: "",
  price: "",
  shipping_price: "",
  total_price: "",
  payment_method: "",
  eta: "",
  tracking_number: "",
  received_date: "",
  notes: "",
  quantity: "",
  created_at: "",
  updated_at: "",
  customer: "",
};

const PartsRow = ({
  handleSearch = () => {},
  data,
  select,
  columns,
  index,
  ...otherProps
}) => {
  const { updateOpenPart } = useOpenParts();
  const { getTableComponents } = useReturnTableComponents();
  const { fetchPartOrders } = useContext(OpenPartOrdersContext);
  const { selectedOpenPartOrders, setSelectedOpenPartOrders } = useContext(
    SelectedOpenPartOrdersContext
  );
  const { vendors } = useContext(VendorsContext);
  const [openOrdersColumns, setOpenOrdersColumns] = useState(
    DEFAULT_OPEN_ORDERS_COLUMNS
  ); // [
  const [formFields, setFormFields] = useState(DEFAULT_FORM_FIELDS);
  const {
    requested_date,
    ticket_number,
    link,
    vendor_part_number,
    description,
    warranty_type,
    status,
    ordered_date,
    vendor,
    po_number,
    price,
    shipping_price,
    total_price,
    payment_method,
    eta,
    tracking_number,
    received_date,
    notes,
    quantity,
    created_at,
    updated_at,
    customer,
  } = formFields;
  useEffect(() => {
    setFormFields({ ...DEFAULT_FORM_FIELDS, ...data });
  }, [data]);
  useEffect(() => {
    if (vendors?.length > 0) {
      setOpenOrdersColumns(
        openOrdersColumns.map((column) => {
          if (column.id === "vendor") {
            return {
              ...column,
              dropdownOptions: vendors?.map((v) => ({
                value: v._id,
                label: v.name,
              })),
            };
          } else {
            return column;
          }
        })
      );
    }
  }, [vendors]);
  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormFields({ ...formFields, [name]: value });
    await updateOpenPart({ ...formFields, [name]: value });
    if (name === "status") fetchPartOrders();
  };
  const handleSelect = (e) => {
    const existingPart = selectedOpenPartOrders.find(
      (p) => p._id === formFields._id
    );
    if (existingPart) {
      setSelectedOpenPartOrders(
        selectedOpenPartOrders.filter((p) => p._id !== formFields._id)
      );
    } else {
      setSelectedOpenPartOrders([...selectedOpenPartOrders, formFields]);
    }
  };
  const formProps = {
    customer: {
      type: "text",
      props: {
        label: "Customer",
        value: data?.customer?.business_name || "",
        name: "customer",
        readOnly: true,
        handleSearch: handleSearch,
        search: true,
      },
    },
    ticket_number: {
      type: "link",
      props: {
        label: "Ticket #",
        text: ticket_number,
        name: "ticket_number",
        link: `https://techcyclesolutions.repairshopr.com/tickets/${data?.ticket_id}`,
      },
    },
    status: {
      type: "selection",
      props: {
        label: "Status",
        value: status,
        name: "status",
        options: ORDERED_PART_STATUSES,
        className: "w-96",
        onChange: handleChange,
      },
    },
    vendor: {
      type: "selection",
      props: {
        label: "Vendor",
        value: vendor,
        name: "vendor",
        className: "w-96",
        options: vendors.map((v) => ({ value: v._id, label: v.name })),
        onChange: handleChange,
      },
    },
    vendor_part_number: {
      type: "text",
      props: {
        label: "Vendor Part #",
        value: vendor_part_number,
        name: "vendor_part_number",
        onChange: handleChange,
        className: "w-96",
      },
    },
    description: {
      type: "text",
      props: {
        label: "Part Description",
        value: description,
        name: "description",
        onChange: handleChange,
        className: "w-96",
        handleSearch: handleSearch,
        search: true,
      },
    },
    po_number: {
      type: "text",
      props: {
        label: "PO Number",
        value: po_number,
        name: "po_number",
        onChange: handleChange,
        className: "w-96",
      },
    },
    quantity: {
      type: "number",
      props: {
        label: "Quantity",
        value: quantity,
        name: "quantity",
        onChange: handleChange,
        className: "w-48",
      },
    },
    price: {
      type: "number",
      props: {
        label: "Price",
        value: price,
        name: "price",
        type: "currency",
        step: "0.01",
        onChange: handleChange,
        className: "w-48",
      },
    },
    shipping_price: {
      type: "number",
      props: {
        label: "Shipping Price",
        value: shipping_price,
        name: "shipping_price",
        type: "currency",
        step: "0.01",
        onChange: handleChange,
        className: "w-48",
      },
    },
    total_price: {
      type: "number",
      props: {
        label: "Total Price",
        value: total_price,
        name: "total_price",
        type: "currency",
        step: "0.01",
        onChange: handleChange,
        className: "w-48",
        readOnly: true,
      },
    },
    payment_method: {
      type: "selection",
      props: {
        label: "Payment Method",
        value: payment_method,
        name: "payment_method",
        options: PAYMENT_METHODS,
        onChange: handleChange,
        className: "w-48",
      },
    },
    tracking_number: {
      type: "text",
      props: {
        label: "Tracking",
        value: tracking_number,
        name: "tracking_number",
        onChange: handleChange,
        className: "w-96",
      },
    },
    eta: {
      type: "date",
      props: {
        label: "ETA",
        value: eta,
        name: "eta",
        onChange: handleChange,
        className: "w-48",
      },
    },
    ordered_date: {
      type: "date",
      props: {
        label: "Ordered Date",
        value: ordered_date,
        name: "ordered_date",
        onChange: handleChange,
        className: "w-48",
      },
    },
    notes: {
      type: "text",
      props: {
        label: "Notes",
        value: notes,
        name: "notes",
        onChange: handleChange,
      },
    },
    createdAt: {
      type: "date",
      props: {
        label: "Created At",
        value: created_at,
        name: "created_at",
        className: "w-48",
        readOnly: true,
      },
    },
    updatedAt: {
      type: "date",
      props: {
        label: "Updated At",
        value: updated_at,
        name: "updated_at",
        className: "w-48",
        readOnly: true,
      },
    },
    received_date: {
      type: "date",
      props: {
        label: "Received Date",
        value: received_date,
        name: "received_date",
        onChange: handleChange,
        className: "w-48",
      },
    },
    requested_date: {
      type: "date",
      props: {
        label: "Requested Date",
        value: requested_date,
        name: "requested_date",
        onChange: handleChange,
        className: "w-48",
        readOnly: true,
      },
    },
    link: {
      type: "link",
      props: {
        link: link,
        text: link,
        name: "link",
        className: "w-96",
      },
    },
    warranty_type: {
      type: "selection",
      props: {
        label: "Warranty Type",
        value: warranty_type,
        name: "warranty_type",
        options: WARRANTY_TYPES,
        onChange: handleChange,
        className: "w-96",
      },
    },
  };
  const showColumn = (column) => {
    return column.show && formProps[column.id];
  };
  return (
    <tr
      className={`${index % 2 === 0 ? "bg-slate-100" : "bg-slate-300"}`}
      {...otherProps}
    >
      {select && (
        <td>
          <input
            type="checkbox"
            name="select-open-part-order"
            checked={
              !!selectedOpenPartOrders.find((p) => p._id === formFields._id)
            }
            className="checked:accent-primary h-full mx-4 my-5 items-center"
            onChange={handleSelect}
          />
        </td>
      )}
      {getTableComponents({
        fields: columns
          .filter(showColumn)
          .map((column) => formProps[column.id]),
      })}
    </tr>
  );
};

export default PartsRow;
